import { panelHeaderControlClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { PanelRightClose, PanelRightOpen } from "lucide-react";

/**
 * Collapses and reopens the canvas — the column beside the page, where a
 * presented artifact, event, or contact appears.
 *
 * The counterpart to {@link SidebarToggleButton}, and it sits beside it: both
 * are controls for a column rather than for anything on the page between them,
 * so the header's leading slot holds the pair. That slot is also the only one
 * every page shares — the pivot panels, the calendar, and a thread each build
 * their own header otherwise — so putting it here is what lets one control serve
 * all of them instead of eight copies threaded through eight pages.
 *
 * Read the chevron, not the icon's suffix: `PanelRightClose` points right (the
 * column retreats), `PanelRightOpen` points left (it grows). The label names the
 * action rather than the state.
 */
export const CanvasToggleButton = ({ open, onToggle }: { open: boolean; onToggle: () => void }) => {
  const label = open ? "Close canvas" : "Open canvas";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={panelHeaderControlClass}
          aria-label={label}
          aria-expanded={open}
          data-statsig-name="nav-canvas-toggle"
          onClick={onToggle}
        >
          {open ? <PanelRightClose /> : <PanelRightOpen />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};
